'use client';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { newsletterSchema, type NewsletterFormData } from '@/lib/schemas';
import { useLeadSubmit } from '@/hooks/useLeadSubmit';
import { Input } from '@/components/ui/Input';
import { Checkbox } from '@/components/ui/Checkbox';
import { Button } from '@/components/ui/Button';
import { NEWSLETTER_CONTENT } from '@/content/footer';
import { LEAD_FORM_VALIDATION } from '@/lib/form-validation';

interface NewsletterFormProps {
  /**
   * Heading shown above the email field. Defaults to the footer newsletter
   * heading; pass `null` to hide it (e.g. when the surrounding section already
   * provides its own heading, like the blog "Subscribe" section).
   */
  heading?: string | null;
  /** Extra classes for the <form> element. */
  className?: string;
}

/**
 * Newsletter subscription form with email, consent checkbox, and honeypot.
 * Submits to /api/newsletter.
 */
export function NewsletterForm({
  heading,
  className,
}: NewsletterFormProps = {}) {
  const headingText = heading === undefined ? NEWSLETTER_CONTENT.heading : heading;
  /**
   * Uses the shared submission pipeline, but reports newsletter_subscribe
   * rather than generate_lead. A subscriber is not a project enquiry.
   */
  const { submitState, submit, notifyStarted } = useLeadSubmit({
    formName: 'newsletter',
    endpoint: '/api/newsletter',
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<NewsletterFormData>({
    ...LEAD_FORM_VALIDATION,
    resolver: zodResolver(newsletterSchema),
  });

  async function onSubmit(data: NewsletterFormData) {
    const accepted = await submit(data);
    if (accepted) reset();
  }

  if (submitState === 'success') {
    return (
      <p className="text-body text-accent">
        Thank you for subscribing!
      </p>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={className ?? 'flex flex-col gap-5'} noValidate>
      {/* Honeypot field — hidden from real users */}
      <div className="absolute -left-[9999px]" aria-hidden="true">
        <input type="text" tabIndex={-1} autoComplete="off" {...register('honeypot')} />
      </div>

      <div className="flex flex-col gap-3">
        {headingText && (
          <p className="text-h4 tracking-tight text-foreground">
            {headingText}
          </p>
        )}
        {headingText && (
          <p className="max-w-[360px] text-[14px] font-light leading-[1.45] text-muted lg:text-[15px]">
            {NEWSLETTER_CONTENT.valueProp}
          </p>
        )}
        <Input
          placeholder={NEWSLETTER_CONTENT.placeholder}
          aria-label={NEWSLETTER_CONTENT.emailAriaLabel}
          type="email"
          error={errors.email?.message}
          aria-required="true"
          {...register('email')}
          onFocus={notifyStarted}
        />
      </div>

      <Checkbox
        label={
          <span>
            {NEWSLETTER_CONTENT.consentText}{' '}
            <a
              href={NEWSLETTER_CONTENT.consentLinkHref}
              className="underline transition-colors hover:text-foreground"
            >
              {NEWSLETTER_CONTENT.consentLinkText}
            </a>
            .
          </span>
        }
        error={errors.consent?.message}
        aria-required="true"
        {...register('consent')}
          onFocus={notifyStarted}
      />

      <Button
        type="submit"
        variant="primary"
        disabled={isSubmitting}
        className="min-h-[48px] w-full rounded-full px-8 uppercase tracking-[-0.02em] sm:w-fit"
      >
        {isSubmitting ? 'Submitting...' : NEWSLETTER_CONTENT.submitLabel}
      </Button>

      {submitState === 'error' && (
        <p className="text-small text-red-500" role="alert">
          Something went wrong. Please try again.
        </p>
      )}
    </form>
  );
}
